import { useEffect, useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../hooks/useAuth";
import {
  registerProfessionalApi,
  registerOrgApi,
  createSubscriptionOrderApi,
  verifySubscriptionPaymentApi,
  validateCouponApi,
} from "../api/verification";
import { AadhaarVerification } from "../components/verification/AadhaarVerification";
import { PanVerification } from "../components/verification/PanVerification";
import { GstVerification } from "../components/verification/GstVerification";
import { passwordProblem } from "../utils/passwordPolicy";
import { formatRupees } from "../utils/money";
import { usePrice } from "../hooks/usePrice";
import { PayerPhoneField } from "../components/PayerPhoneField";
import { payerMobile, usePayerPhone } from "../hooks/usePayerPhone";
import { InlineIcon } from "../components/InlineIcon";
import {
  BrandButton,
  BrandCard,
  StepIntro,
  StepProgress,
  OptionTiles,
  VerifiedBanner,
  AmountSummaryBox,
} from "../components/brand";
import { C, font, radii, shadows, space, type } from "../styles/tokens";

function ShieldIcon({ size = 20, strokeWidth = 2, color = "currentColor", ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function BuildingIcon({ size = 20, strokeWidth = 2, color = "currentColor", ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
      <rect width="16" height="20" x="4" y="2" rx="2" ry="2" />
      <path d="M9 22v-4h6v4" />
      <path d="M8 6h.01" />
      <path d="M16 6h.01" />
      <path d="M8 10h.01" />
      <path d="M16 10h.01" />
      <path d="M8 14h.01" />
      <path d="M16 14h.01" />
    </svg>
  );
}

function FileTextIcon({ size = 20, strokeWidth = 2, color = "currentColor", ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M10 9H8" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
    </svg>
  );
}

function CreditCardIcon({ size = 20, strokeWidth = 2, color = "currentColor", ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
      <rect width="20" height="14" x="2" y="5" rx="2" />
      <line x1="2" x2="22" y1="10" y2="10" />
    </svg>
  );
}

function KeyIcon({ size = 20, strokeWidth = 2, color = "currentColor", ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
      <path d="m21 2-2 2m-1.5 1.5L14 9l-4-4-6 6a5.5 5.5 0 0 0 7.8 7.8l6-6 3.5 3.5 2-2-2-2 2-2-2-2Z" />
    </svg>
  );
}

function CheckIcon({ size = 20, strokeWidth = 2, color = "currentColor", ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function LockIcon({ size = 20, strokeWidth = 2, color = "currentColor", ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

function loadCashfreeScript() {
  return new Promise((resolve) => {
    if (window.Cashfree) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

const INDIAN_MOBILE = /^[6-9]\d{9}$/;

// A 10-digit Indian mobile from free text (accepts a +91 or 0 prefix), or ""
// when the value is not one. Signup never invents a number: an empty result
// means the account simply has no phone on record.
function normaliseMobile(raw) {
  let digits = String(raw ?? "").replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
  else if (digits.length === 11 && digits.startsWith("0"))
    digits = digits.slice(1);
  return INDIAN_MOBILE.test(digits) ? digits : "";
}

// The mobile number returned by the identity provider, if it returned one.
function providerPhoneOf(payload) {
  const phone = normaliseMobile(
    payload?.phone ||
      payload?.mobile ||
      payload?.contactPhone ||
      payload?.signatoryPhone,
  );
  // 9876543210 is a known placeholder number used by legacy/mock records;
  // treat it as not provided so the user can enter their real mobile number.
  if (phone === "9876543210") return "";
  return phone;
}

const ROLE_CONFIG = {
  PROFESSIONAL: {
    headerTitle: "Sign up",
    headerSubtitle:
      "Verify your Aadhaar identity, then set up account credentials.",
    completeButton: "Complete Registration",
    nextRoute: "/profile",
  },
  INSTITUTION_OFFICER: {
    headerTitle: "Register as an Academic Institution",
    headerSubtitle:
      "Verify your academic institution identity (GSTIN or PAN), complete subscription payment, then set up officer credentials.",
    completeButton: "Submit Institution Registration",
    nextRoute: "/awaiting-approval",
  },
  ORGANISATION_OFFICER: {
    headerTitle: "Register as an Organisation",
    headerSubtitle:
      "Verify your organisation identity (GSTIN or PAN), complete subscription payment, then set up officer credentials.",
    completeButton: "Submit Organisation Registration",
    nextRoute: "/awaiting-approval",
  },
};

export function Register({ role: propRole, defaultMethod = "GST" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryRole = searchParams.get("role");
  const rawRole =
    queryRole ||
    (propRole && propRole !== "PROFESSIONAL" ? propRole : null) ||
    (location.pathname.includes("organisation")
      ? "ORGANISATION_OFFICER"
      : location.pathname.includes("institution")
        ? "INSTITUTION_OFFICER"
        : propRole || "PROFESSIONAL");

  const normalizedRole = (() => {
    const r = String(rawRole || "").toUpperCase();
    if (r.includes("INSTITUTION") || r.includes("ACADEMIC"))
      return "INSTITUTION_OFFICER";
    if (r.includes("ORG") || r.includes("COMPANY") || r.includes("BUSINESS"))
      return "ORGANISATION_OFFICER";
    return "PROFESSIONAL";
  })();
  const role = normalizedRole;
  //const t = useT();
  const { login } = useAuth();
  const roleConfig = ROLE_CONFIG[role] ?? ROLE_CONFIG.PROFESSIONAL;
  const isOfficer = role !== "PROFESSIONAL";

  const paramStep = searchParams.get("step");
  const initialStep =
    paramStep && ["1", "2", "3"].includes(paramStep)
      ? parseInt(paramStep, 10)
      : 1;
  const [step, setStep] = useState(initialStep); // 1 = Identity Verification, 2 = Payment, 3 = Credentials

  const [form, setForm] = useState({
    verificationToken: "",
    fullName: "",
    dob: "",
    email: "",
    phone: "",
    altPhone: "",
    password: "",
    orgName: "",
    orgRegistrationNumber: "",
    addressLine1: "",
    city: "",
    state: "",
    pincode: "",
    couponCode: "",
    logoUrl: "",
    logoFile: null,
    description: "",
    termsAccepted: false,
  });

  const [verifiedPayload, setVerifiedPayload] = useState(null);
  // Whether the account holder's name comes fixed from the verification.
  const [nameLocked, setNameLocked] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});
  const [completing, setCompleting] = useState(false);
  const [stepError, setStepError] = useState(null);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    if (fieldErrors[field])
      setFieldErrors((e) => ({ ...e, [field]: undefined }));
  }

  const handleVerificationSuccess = (payload) => {
    setVerifiedPayload(payload);
    const providerPhone = providerPhoneOf(payload);

    // The admin's name: the GST record's signatory, fixed, when it names
    // one; otherwise the admin types their own (the name on a company's PAN
    // is the company's, not the admin's; an individual's PAN is theirs, to
    // check). Proposal 2.3, as the app does.
    const officerSignatoryName =
      payload.primarySignatoryName ||
      payload.signatoryName ||
      (Array.isArray(payload.signatories) && payload.signatories[0]?.name) ||
      "";
    const individualPanName =
      payload.pan && !payload.gstin && payload.category === "P"
        ? payload.name || payload.nameOnPan || ""
        : "";
    setNameLocked(!isOfficer || Boolean(officerSignatoryName));

    const resolvedFullName = isOfficer
      ? officerSignatoryName || individualPanName
      : payload.name ||
        payload.legalName ||
        payload.registeredName ||
        payload.nameOnPan ||
        "";

    const addressLine1 = payload.addressLine1 || payload.address || "";
    const city = payload.city || "";
    const state = payload.state || "";
    const pincode = payload.pincode || "";

    setForm((f) => ({
      ...f,
      verificationToken: payload.verificationToken || "",
      fullName: resolvedFullName || f.fullName,
      phone: providerPhone || f.phone,
      dob: payload.dob || payload.dateOfBirth || f.dob,
      orgName:
        payload.legalName ||
        payload.tradeName ||
        payload.registeredName ||
        payload.nameOnPan ||
        f.orgName,
      orgRegistrationNumber:
        payload.pan || payload.gstin || f.orgRegistrationNumber,
      addressLine1: addressLine1 || f.addressLine1,
      city: city || f.city,
      state: state || f.state,
      pincode: pincode || f.pincode,
    }));
    // ===== PROFESSIONAL SIGNUP PAYMENT STEP DISABLED -- see RE-ENABLE note below =====
    // Disabled on: 2026-09-12. Reason: Professional signup payment bypassed / complimentary access.
    // To re-enable: uncomment setStep(2) for Professional accounts below, and see matching commented blocks in:
    //   - packages/backend/src/identity-verification/identity-verification.service.ts
    //   - packages/frontend/src/pages/BillingPage.jsx
    //   - docs/TEMPORARY_CHANGES.md (full restoration checklist)
    // setStep(2); // Proceed to Subscription Payment step
    // ===== END DISABLED BLOCK =====

    // ACTIVE BYPASS LOGIC: Professional accounts skip Step 2 and proceed directly to Credentials (Step 3)
    if (!isOfficer) {
      setStep(3);
    } else {
      setStep(2); // Organisation/Academic Institution still requires payment
    }
  };

  useEffect(() => {
    if (location.state?.verificationToken) {
      handleVerificationSuccess(location.state);
    }
  }, [location.state]);

  const handlePaymentSuccess = () => {
    setStep(3); // Proceed to Account Credentials step
  };

  const handleCompleteRegistration = async (e) => {
    if (e) e.preventDefault();
    setStepError(null);

    const errors = {};
    if (
      !form.email?.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
    ) {
      errors.email = "Please enter a valid email address.";
    }
    // Optional: blank is fine, but anything typed must be a real mobile.
    if (form.phone?.trim() && !normaliseMobile(form.phone)) {
      errors.phone =
        "Enter a 10-digit Indian mobile number, or leave this blank.";
    }
    const passwordError = passwordProblem(form.password);
    if (passwordError) {
      errors.password = passwordError;
    }
    if (!form.fullName?.trim()) {
      errors.fullName = "Full name is required.";
    }
    if (isOfficer && !form.orgName?.trim()) {
      errors.orgName = "Organisation / Institution name is required.";
    }
    if (!form.termsAccepted) {
      errors.terms = "You must accept the Terms of Service to proceed.";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    if (!form.verificationToken) {
      setStepError(
        "Identity verification token is missing. Please verify your identity again.",
      );
      setStep(1);
      return;
    }

    setCompleting(true);

    try {
      let regResult;
      const cleanEmail = form.email.trim().toLowerCase();
      const cleanPhone = normaliseMobile(form.phone);
      const phone = cleanPhone ? `+91${cleanPhone}` : undefined;

      if (!isOfficer) {
        // Professional registration
        regResult = await registerProfessionalApi({
          verificationToken: form.verificationToken,
          email: cleanEmail,
          password: form.password,
          fullName: form.fullName.trim(),
          phone,
        });
      } else {
        // Institution / Organisation registration
        const accountType =
          role === "INSTITUTION_OFFICER"
            ? "ACADEMIC_INSTITUTION"
            : "ORGANISATION";
        regResult = await registerOrgApi({
          verificationToken: form.verificationToken,
          email: cleanEmail,
          password: form.password,
          fullName: form.fullName.trim(),
          phone,
          accountType,
          orgName: form.orgName.trim(),
          addressLine1: form.addressLine1.trim() || undefined,
          city: form.city.trim() || undefined,
          state: form.state.trim() || undefined,
          pincode: form.pincode.trim() || undefined,
          description: form.description?.trim() || undefined,
          couponCode: form.couponCode
            ? form.couponCode.trim().toUpperCase()
            : undefined,
        });
      }

      if (regResult?.success) {
        // Auto-login via custom auth login endpoint
        await login(cleanEmail, form.password);

        const nextParam = searchParams.get("next");
        const destination =
          nextParam ||
          (!isOfficer
            ? "/profile"
            : regResult?.isApproved
              ? "/dashboard?onboarding=true"
              : "/awaiting-approval");
        navigate(destination, { replace: true });
      } else {
        throw new Error(regResult?.message || "Registration failed.");
      }
    } catch (err) {
      const msg =
        err?.message || err?.response?.data?.message || "Registration failed.";
      setStepError(msg);

      // If payment missing or token expired/used, redirect to appropriate step
      if (msg.toLowerCase().includes("payment")) {
        setStep(isOfficer ? 2 : 1);
      } else if (
        msg.toLowerCase().includes("expired") ||
        msg.toLowerCase().includes("purpose mismatch") ||
        msg.toLowerCase().includes("already been used")
      ) {
        setTimeout(() => {
          setStep(1);
        }, 3000);
      }
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: C.background,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
        fontFamily: font,
        color: C.textStrong,
        boxSizing: "border-box",
      }}
    >
      <main style={{ width: "100%", maxWidth: 640, margin: "0 auto" }}>
        {/* Centered Brand Mark per Section 7 */}
        <div style={{ textAlign: "center", marginBottom: space.xl }}>
          <Link to="/" style={{ display: "inline-block" }}>
            <img
              src="/brand-mark.png"
              alt="TickAndPass"
              style={{ height: 32, width: "auto", objectFit: "contain" }}
            />
          </Link>
        </div>

        <BrandCard style={{ padding: "28px 24px" }}>
          {step === 1 && (
            <Step1Verification
              role={role}
              isOfficer={isOfficer}
              form={form}
              update={update}
              onVerificationSuccess={handleVerificationSuccess}
              defaultMethod={defaultMethod || "GST"}
            />
          )}

          {isOfficer && step === 2 && (
            <Step2SubscriptionPayment
              role={role}
              isOfficer={isOfficer}
              verificationToken={form.verificationToken}
              verifiedPayload={verifiedPayload}
              form={form}
              update={update}
              onPaymentSuccess={handlePaymentSuccess}
              onBackToVerification={() => setStep(1)}
            />
          )}

          {step === 3 && (
            <Step3AccountSetup
              role={role}
              isOfficer={isOfficer}
              form={form}
              update={update}
              fieldErrors={fieldErrors}
              verifiedPayload={verifiedPayload}
              nameLocked={nameLocked}
              onSubmit={handleCompleteRegistration}
              completing={completing}
              error={stepError}
              roleConfig={roleConfig}
              onBackToPayment={() => setStep(isOfficer ? 2 : 1)}
            />
          )}
        </BrandCard>
      </main>
    </div>
  );
}

function Step1Verification({
  role,
  isOfficer,
  form,
  update,
  onVerificationSuccess,
  defaultMethod = "GST",
}) {
  const [verificationMethod, setVerificationMethod] = useState(
    defaultMethod || "GST",
  ); // "GST" | "PAN"
  const orgLabel =
    role === "ORGANISATION_OFFICER" ? "Organisation" : "Academic Institution";

  return (
    <>
      <StepIntro
        step={1}
        total={isOfficer ? 3 : 2}
        Icon={isOfficer ? BuildingIcon : ShieldIcon}
        badges={[
          { key: "check", Icon: CheckIcon, tone: "success" },
          { key: "lock", Icon: LockIcon, tone: "info" },
          { key: "shield", Icon: ShieldIcon, tone: "warning" },
        ]}
        title={
          isOfficer
            ? role === "INSTITUTION_OFFICER"
              ? "Academic Institution Onboarding"
              : "Organisation Onboarding"
            : "Professional Sign Up"
        }
        body={
          isOfficer
            ? `Authenticate your ${orgLabel.toLowerCase()} identity before proceeding.`
            : "Authenticate your Aadhaar identity via Cashfree OTP or DigiLocker eKYC before proceeding."
        }
      />

      {isOfficer && (
        <OptionTiles
          label="Verification Document"
          value={verificationMethod}
          onChange={(val) => {
            setVerificationMethod(val);
            update("orgRegistrationNumber", "");
          }}
          options={[
            {
              key: "GST",
              title: "GSTIN",
              detail: "Verify via Government GSTN OTP",
              Icon: BuildingIcon,
            },
            {
              key: "PAN",
              title: "Company PAN",
              detail: "Verify via Corporate PAN Card",
              Icon: FileTextIcon,
            },
          ]}
        />
      )}

      <div>
        {isOfficer ? (
          <div>
            {verificationMethod === "GST" ? (
              <GstVerification
                accountType={
                  role === "ORGANISATION_OFFICER"
                    ? "ORGANISATION"
                    : "ACADEMIC_INSTITUTION"
                }
                gstin={form.orgRegistrationNumber || ""}
                onGstinChange={(val) => update("orgRegistrationNumber", val)}
                onVerificationSuccess={onVerificationSuccess}
                defaultEmail={form.email || ""}
                onSwitchToPan={() => {
                  setVerificationMethod("PAN");
                  update("orgRegistrationNumber", "");
                }}
              />
            ) : (
              <PanVerification
                accountType={
                  role === "ORGANISATION_OFFICER"
                    ? "ORGANISATION"
                    : "ACADEMIC_INSTITUTION"
                }
                pan={form.orgRegistrationNumber || ""}
                onPanChange={(val) => update("orgRegistrationNumber", val)}
                onVerificationSuccess={onVerificationSuccess}
                defaultEmail={form.email || ""}
                onSwitchToGst={() => {
                  setVerificationMethod("GST");
                  update("orgRegistrationNumber", "");
                }}
              />
            )}
          </div>
        ) : (
          <AadhaarVerification
            aadhaarNumber={form.aadhaar || ""}
            onAadhaarChange={(val) => update("aadhaar", val)}
            onVerificationSuccess={onVerificationSuccess}
          />
        )}

        <div
          style={{
            marginTop: space.lg,
            paddingTop: space.md,
            borderTop: `1px solid ${C.divider}`,
            textAlign: "center",
            fontSize: type.caption.fontSize,
            color: C.textMuted,
          }}
        >
          Already have an account?{" "}
          <Link
            to="/login"
            style={{
              color: C.primary,
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            Sign in with email &amp; password
          </Link>
        </div>
      </div>
    </>
  );
}

function Step2SubscriptionPayment({
  role,
  isOfficer,
  verificationToken,
  verifiedPayload,
  form,
  update,
  onPaymentSuccess,
  onBackToVerification,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedGateway, setSelectedGateway] = useState("cashfree"); // "cashfree" | "razorpay"

  // Pre-fill phone if available from verification or earlier inputs
  const initialPhone =
    normaliseMobile(form?.phone) ||
    normaliseMobile(verifiedPayload?.phone) ||
    normaliseMobile(verifiedPayload?.mobile) ||
    "";
  const [payerPhone, setPayerPhone] = useState(initialPhone);
  const [phoneError, setPhoneError] = useState(null);

  useEffect(() => {
    if (!payerPhone) {
      const resolved =
        normaliseMobile(form?.phone) ||
        normaliseMobile(verifiedPayload?.phone) ||
        normaliseMobile(verifiedPayload?.mobile) ||
        "";
      if (resolved) {
        setPayerPhone(resolved);
      }
    }
  }, [form?.phone, verifiedPayload]);

  const handlePhoneChange = (val) => {
    const cleaned = val.replace(/\D/g, "").slice(0, 10);
    setPayerPhone(cleaned);
    if (phoneError) setPhoneError(null);
    if (update) update("phone", cleaned);
  };

  // One-time onboarding coupon code state
  const [couponCodeInput, setCouponCodeInput] = useState(
    form?.couponCode || "",
  );
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState(null);
  const [couponSuccessMsg, setCouponSuccessMsg] = useState(null);

  const isCouponApplied = Boolean(appliedCoupon && appliedCoupon.valid);
  const isFreeCoupon = Boolean(
    isCouponApplied &&
    (appliedCoupon.discountPercent === 100 ||
      appliedCoupon.finalAmountPaise === 0),
  );

  const handleApplyCoupon = async (e) => {
    if (e) e.preventDefault();
    const cleanCode = couponCodeInput.trim().toUpperCase();
    if (!cleanCode) {
      setCouponError("Please enter a valid coupon code.");
      return;
    }
    setCouponError(null);
    setCouponSuccessMsg(null);
    setCouponLoading(true);

    try {
      const res = await validateCouponApi({
        code: cleanCode,
        accountType,
        verificationToken,
      });
      const data = res?.data || res;
      if (!data?.valid) {
        throw new Error(data?.message || "Invalid coupon code.");
      }
      setAppliedCoupon(data);
      const discountPct = data.discountPercent ?? 100;
      setCouponSuccessMsg(
        data.message ||
          (discountPct === 100
            ? `Coupon ${data.code} applied! 100% onboarding fee waived.`
            : `Coupon ${data.code} applied! ${discountPct}% discount applied.`),
      );
      if (update) update("couponCode", data.code);
    } catch (err) {
      setAppliedCoupon(null);
      setCouponError(err?.message || "Failed to validate coupon code.");
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCodeInput("");
    setCouponError(null);
    setCouponSuccessMsg(null);
    if (update) update("couponCode", "");
  };

  const accountType =
    role === "PROFESSIONAL"
      ? "PROFESSIONAL"
      : role === "INSTITUTION_OFFICER"
        ? "ACADEMIC_INSTITUTION"
        : "ORGANISATION";

  const isProf = accountType === "PROFESSIONAL";
  // The price the server will charge, read before the order.
  const price = usePrice(
    isProf
      ? "PROFESSIONAL_MEMBERSHIP"
      : role === "INSTITUTION_OFFICER"
        ? "INSTITUTION_SUBSCRIPTION"
        : "ORGANISATION_SUBSCRIPTION",
  );
  const feeDisplay = price.pricePaise
    ? formatRupees(price.pricePaise)
    : (price.error ?? "Loading...");
  const periodDisplay = "/ year";

  const payableAmountDisplay = useMemo(() => {
    if (!isCouponApplied) return feeDisplay || "₹12,000";
    if (isFreeCoupon) return "₹0";
    if (typeof appliedCoupon?.finalAmountPaise === "number") {
      return formatRupees(appliedCoupon.finalAmountPaise);
    }
    const basePaise = price.pricePaise || 1200000;
    const discounted = Math.round(
      basePaise * (1 - (appliedCoupon?.discountPercent || 0) / 100),
    );
    return formatRupees(discounted);
  }, [
    isCouponApplied,
    isFreeCoupon,
    appliedCoupon,
    feeDisplay,
    price.pricePaise,
  ]);

  const handlePay = async () => {
    const cleanMobile = normaliseMobile(payerPhone);
    if (!cleanMobile) {
      setPhoneError(
        "Please enter a valid 10-digit Indian mobile number to proceed.",
      );
      return;
    }
    setPhoneError(null);
    setLoading(true);
    setError(null);

    try {
      // 1. Request subscription order from backend
      const order = await createSubscriptionOrderApi({
        verificationToken,
        accountType,
        gatewayProvider: selectedGateway,
        payerPhone: cleanMobile,
        couponCode: isCouponApplied ? appliedCoupon?.code : undefined,
      });

      if (order?.alreadyPaid || order?.bypass) {
        if (update) update("phone", cleanMobile);
        onPaymentSuccess();
        return;
      }

      const resolvedOrderId =
        order?.orderId ||
        order?.data?.orderId ||
        order?.gatewayOrderId ||
        order?.data?.gatewayOrderId ||
        order?.razorpayOrderId;
      const resolvedSessionId =
        order?.paymentSessionId || order?.data?.paymentSessionId;
      const resolvedProvider =
        order?.gatewayProvider ||
        order?.data?.gatewayProvider ||
        selectedGateway;

      if (!resolvedOrderId) {
        throw new Error(
          order?.message ||
            order?.data?.message ||
            "Failed to create subscription payment order.",
        );
      }

      // Check if Cashfree was selected / returned
      if (resolvedProvider === "cashfree" || resolvedSessionId) {
        if (!resolvedSessionId) {
          throw new Error(
            "Cashfree payment session ID is missing from server response. Please try again.",
          );
        }
        const cfLoaded = await loadCashfreeScript();
        if (!cfLoaded || !window.Cashfree) {
          throw new Error(
            "Cashfree Checkout SDK failed to load. Please check your network connection and retry.",
          );
        }
        const isProdEnv =
          (
            import.meta.env.VITE_CASHFREE_ENVIRONMENT ||
            import.meta.env.VITE_CASHFREE_MODE ||
            ""
          ).toLowerCase() === "production" ||
          (typeof window !== "undefined" &&
            (window.location.hostname === "tickandpass.com" ||
              window.location.hostname === "www.tickandpass.com"));

        const cfMode = isProdEnv ? "production" : "sandbox";
        const cashfree = window.Cashfree({ mode: cfMode });

        const checkoutOptions = {
          paymentSessionId: resolvedSessionId,
          redirectTarget: "_modal",
        };

        cashfree
          .checkout(checkoutOptions)
          .then(async (result) => {
            if (result?.error) {
              setLoading(false);
              if (
                result.error.code === "MODAL_CLOSED" ||
                result.error.message?.toLowerCase().includes("closed")
              ) {
                setError(
                  "Payment modal was closed. You can retry whenever you are ready.",
                );
                return;
              }
              setError(
                result.error.message ||
                  "Payment could not be completed via Cashfree.",
              );
              return;
            }

            if (result?.redirect) {
              return;
            }

            // Payment completed in modal: verify on backend
            try {
              setLoading(true);
              const payId =
                result?.paymentDetails?.paymentId ||
                result?.paymentDetails?.payment_id ||
                resolvedOrderId;

              const verifyResult = await verifySubscriptionPaymentApi({
                verificationToken,
                gatewayProvider: "cashfree",
                gatewayOrderId: resolvedOrderId,
                gatewayPaymentId: payId,
              });

              if (verifyResult?.success) {
                if (update) update("phone", cleanMobile);
                onPaymentSuccess();
              } else {
                throw new Error(
                  verifyResult?.message ||
                    "Cashfree payment verification failed on server.",
                );
              }
            } catch (verifyErr) {
              setError(
                verifyErr?.message ||
                  "Payment verification failed. Please try again.",
              );
            } finally {
              setLoading(false);
            }
          })
          .catch((cfErr) => {
            setLoading(false);
            setError(
              cfErr?.message || "Failed to initialize Cashfree Checkout.",
            );
          });
        return;
      }

      // Fallback: Launch Razorpay Checkout if requested
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error(
          "Razorpay SDK failed to load. Please check your network connection and retry.",
        );
      }

      const rzpOrderId = resolvedOrderId;
      const options = {
        key: order.razorpayKeyId || order.keyId,
        amount: order.amountPaise,
        currency: order.currency || "INR",
        name: "TickAndPass Platform",
        description: isProf
          ? "Professional Membership (1 Year)"
          : "Enterprise Subscription (1 Year)",
        order_id: rzpOrderId,
        prefill: {
          name: verifiedPayload?.name || verifiedPayload?.legalName || "",
          contact: cleanMobile ? `+91${cleanMobile}` : undefined,
        },
        theme: {
          color: "#001952",
        },
        handler: async function (response) {
          try {
            const verifyResult = await verifySubscriptionPaymentApi({
              verificationToken,
              gatewayProvider: "razorpay",
              gatewayOrderId: response.razorpay_order_id,
              gatewayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });

            if (verifyResult?.success) {
              if (update) update("phone", cleanMobile);
              onPaymentSuccess();
            } else {
              throw new Error(
                verifyResult?.message ||
                  "Payment signature verification failed.",
              );
            }
          } catch (err) {
            setError(
              err?.message ||
                "Payment verification failed. Please try payment retry.",
            );
          } finally {
            setLoading(false);
          }
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
            setError(
              "Payment modal closed. You can retry with Razorpay or switch to Cashfree.",
            );
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        setLoading(false);
        setError(
          `Payment failed: ${response.error?.description || "Transaction declined."}`,
        );
      });

      rzp.open();
    } catch (err) {
      setLoading(false);
      setError(
        err?.message ||
          "Failed to initiate payment. Please check your details and retry.",
      );
    }
  };

  const verifiedEntityName =
    verifiedPayload?.legalName ||
    verifiedPayload?.registeredName ||
    verifiedPayload?.tradeName ||
    form?.orgName ||
    "Identity Verified";

  const verifiedIdentifier = verifiedPayload?.gstin
    ? `GSTIN: ${verifiedPayload.gstin}`
    : `PAN: ${verifiedPayload?.pan || form?.orgRegistrationNumber || ""}`;

  return (
    <>
      <StepIntro
        compact
        step={2}
        total={3}
        Icon={CreditCardIcon}
        title={
          isProf
            ? "Professional Subscription"
            : role === "INSTITUTION_OFFICER"
              ? "Academic Institution Subscription"
              : "Enterprise Subscription"
        }
        body="Complete your annual onboarding subscription."
      />

      <VerifiedBanner
        eyebrow={verifiedPayload?.gstin ? "GST verified" : "PAN verified"}
        name={verifiedEntityName}
        detail={verifiedIdentifier}
      />

      {/* Pre-payment Disclosure Callout for Platform Admin Review */}
      {isOfficer && (
        <div
          style={{
            backgroundColor: C.warningTint,
            color: C.warning,
            padding: space.md,
            borderRadius: radii.control,
            marginBottom: space.md,
            fontSize: type.caption.fontSize,
            lineHeight: type.caption.lineHeight,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 2 }}>
            Platform Administrator Review Required
          </div>
          <div>
            Your account will be placed under administrator review after
            subscription payment and credential setup before full portal
            access is granted.
          </div>
        </div>
      )}

      {/* Amount Summary Box per Section 8 */}
      <AmountSummaryBox
        title={
          isProf
            ? "Individual Professional Pass"
            : role === "INSTITUTION_OFFICER"
              ? "Academic Institution Subscription"
              : "Enterprise Organisation Subscription"
        }
        items={[
          { label: "Annual Subscription", amount: feeDisplay || "₹12,000" },
          ...(isCouponApplied
            ? [
                {
                  label: `Promo Coupon (${appliedCoupon?.code})`,
                  amount: isFreeCoupon ? `-${feeDisplay || "₹12,000"}` : `-${formatRupees((price.pricePaise || 1200000) - (appliedCoupon?.finalAmountPaise || 0))}`,
                  detail: `${appliedCoupon?.discountPercent || 100}% discount applied`,
                },
              ]
            : []),
        ]}
        total={{
          label: "To pay",
          amount: payableAmountDisplay,
        }}
        footnote="Subscription fees are non-refundable once verified."
      />

      {/* Coupon Code Section */}
      <div style={{ marginBottom: space.lg }}>
        <label
          style={{
            display: "block",
            fontSize: type.label.fontSize,
            lineHeight: type.label.lineHeight,
            fontWeight: type.label.fontWeight,
            color: C.textMuted,
            marginBottom: space.sm,
          }}
        >
          Have an onboarding coupon?
        </label>

        {isCouponApplied ? (
          <div
            style={{
              padding: space.md,
              backgroundColor: C.successTint,
              borderRadius: radii.control,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: type.bodyStrong.fontSize,
                  fontWeight: type.bodyStrong.fontWeight,
                  color: C.success,
                }}
              >
                {appliedCoupon.code} ({appliedCoupon.discountPercent}% OFF)
              </div>
              <div
                style={{
                  fontSize: type.caption.fontSize,
                  color: C.textMuted,
                  marginTop: 2,
                }}
              >
                {appliedCoupon.message || "Discount applied to onboarding fee."}
              </div>
            </div>
            <button
              type="button"
              onClick={handleRemoveCoupon}
              style={{
                background: "none",
                border: "none",
                color: C.danger,
                fontSize: type.caption.fontSize,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Remove
            </button>
          </div>
        ) : (
          <div>
            <div style={{ display: "flex", gap: space.sm }}>
              <input
                type="text"
                placeholder="Enter coupon code"
                value={couponCodeInput}
                onChange={(e) => {
                  setCouponCodeInput(e.target.value.toUpperCase());
                  if (couponError) setCouponError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleApplyCoupon();
                  }
                }}
                style={{
                  flex: 1,
                  height: 48,
                  boxSizing: "border-box",
                  padding: "0 16px",
                  fontSize: type.body.fontSize,
                  fontFamily: font,
                  color: C.textStrong,
                  backgroundColor: C.surface,
                  border: `1.5px solid ${C.border}`,
                  borderRadius: radii.control,
                  outline: "none",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              />
              <BrandButton
                size="default"
                variant="secondary"
                disabled={couponLoading || !couponCodeInput.trim()}
                onClick={handleApplyCoupon}
              >
                {couponLoading ? "Checking..." : "Apply"}
              </BrandButton>
            </div>
            {couponError && (
              <p
                style={{
                  color: C.danger,
                  fontSize: type.caption.fontSize,
                  marginTop: 4,
                  marginBottom: 0,
                }}
              >
                {couponError}
              </p>
            )}
          </div>
        )}
      </div>

      {isFreeCoupon ? (
        <div>
          <BrandButton
            variant="primary"
            fullWidth
            onClick={() => {
              if (update) update("couponCode", appliedCoupon.code);
              onPaymentSuccess();
            }}
          >
            Continue to Account Credentials (₹0) &rarr;
          </BrandButton>
        </div>
      ) : (
        <div>
          {/* Payer Mobile Field */}
          <div style={{ marginBottom: space.lg }}>
            <label
              style={{
                display: "block",
                fontSize: type.label.fontSize,
                lineHeight: type.label.lineHeight,
                fontWeight: type.label.fontWeight,
                color: C.textMuted,
                marginBottom: space.sm,
              }}
            >
              Payer mobile number (required for payment)
            </label>
            <div
              style={{
                display: "flex",
                height: 48,
                borderRadius: radii.control,
                border: `1.5px solid ${phoneError ? C.danger : C.border}`,
                backgroundColor: C.surface,
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
              <span
                style={{
                  padding: "0 14px",
                  display: "flex",
                  alignItems: "center",
                  fontSize: type.body.fontSize,
                  fontWeight: 600,
                  color: C.textMuted,
                  backgroundColor: C.surfaceSunken,
                  borderRight: `1px solid ${C.divider}`,
                }}
              >
                +91
              </span>
              <input
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit mobile number"
                value={payerPhone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0 14px",
                  fontSize: type.body.fontSize,
                  fontFamily: font,
                  color: C.textStrong,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                }}
              />
            </div>
            {phoneError && (
              <p
                style={{
                  color: C.danger,
                  fontSize: type.caption.fontSize,
                  marginTop: 4,
                  marginBottom: 0,
                }}
              >
                {phoneError}
              </p>
            )}
          </div>

          {error && (
            <div
              style={{
                backgroundColor: C.dangerTint,
                color: C.danger,
                padding: `${space.sm}px ${space.md}px`,
                borderRadius: radii.control,
                marginBottom: space.md,
                fontSize: type.caption.fontSize,
                lineHeight: type.caption.lineHeight,
              }}
            >
              {error}
            </div>
          )}

          <BrandButton
            variant="primary"
            fullWidth
            loading={loading}
            onClick={handlePay}
          >
            Pay {payableAmountDisplay} via Cashfree
          </BrandButton>
        </div>
      )}

      <div style={{ marginTop: space.lg, textAlign: "center" }}>
        <button
          type="button"
          onClick={onBackToVerification}
          style={{
            background: "none",
            border: "none",
            color: C.textMuted,
            fontSize: type.caption.fontSize,
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          &larr; Back to identity verification
        </button>
      </div>
    </>
  );
}

function Step3AccountSetup({
  role,
  isOfficer,
  form,
  update,
  fieldErrors,
  verifiedPayload,
  nameLocked,
  onSubmit,
  completing,
  error,
  roleConfig,
  onBackToPayment,
}) {
  const [showPassword, setShowPassword] = useState(false);
  // Read-only only when the identity provider actually returned a number.
  const providerPhone = providerPhoneOf(verifiedPayload);

  return (
    <form onSubmit={onSubmit}>
      <StepIntro
        compact
        step={isOfficer ? 3 : 2}
        total={isOfficer ? 3 : 2}
        Icon={KeyIcon}
        title="Create your account"
        body="Enter your details to finish registering your account."
      />

      <VerifiedBanner
        eyebrow={isOfficer ? "Entity verified" : "Aadhaar verified"}
        name={
          isOfficer
            ? verifiedPayload?.legalName || form.orgName || "Verified Organisation"
            : verifiedPayload?.name || form.fullName || "Verified Citizen"
        }
        detail={
          isOfficer
            ? form?.couponCode
              ? `100% Promo Coupon (${form.couponCode})`
              : "Identity & onboarding verified at source"
            : "Identity verification confirmed at source"
        }
      />

      <div style={{ display: "flex", flexDirection: "column", gap: space.md }}>
        <div>
          <label
            style={{
              display: "block",
              fontSize: type.label.fontSize,
              lineHeight: type.label.lineHeight,
              fontWeight: type.label.fontWeight,
              color: C.textMuted,
              marginBottom: space.sm,
            }}
          >
            Full Name
          </label>
          {nameLocked ? (
            <input
              type="text"
              placeholder="Full Name"
              value={form.fullName}
              readOnly
              style={{
                width: "100%",
                height: 48,
                boxSizing: "border-box",
                padding: "0 16px",
                fontSize: type.body.fontSize,
                fontFamily: font,
                color: C.textMuted,
                backgroundColor: C.surfaceSunken,
                border: `1.5px solid ${C.border}`,
                borderRadius: radii.control,
                outline: "none",
                cursor: "not-allowed",
              }}
            />
          ) : (
            <input
              type="text"
              placeholder="Your full name"
              autoComplete="name"
              value={form.fullName}
              onChange={(e) => update("fullName", e.target.value)}
              style={{
                width: "100%",
                height: 48,
                boxSizing: "border-box",
                padding: "0 16px",
                fontSize: type.body.fontSize,
                fontFamily: font,
                color: C.textStrong,
                backgroundColor: C.surface,
                border: `1.5px solid ${fieldErrors?.fullName ? C.danger : C.border}`,
                borderRadius: radii.control,
                outline: "none",
              }}
            />
          )}
          {fieldErrors?.fullName && (
            <p
              style={{
                color: C.danger,
                fontSize: type.caption.fontSize,
                marginTop: 4,
                marginBottom: 0,
              }}
            >
              {fieldErrors.fullName}
            </p>
          )}
        </div>

        {!isOfficer && (
          <div>
            <label
              style={{
                display: "block",
                fontSize: type.label.fontSize,
                lineHeight: type.label.lineHeight,
                fontWeight: type.label.fontWeight,
                color: C.textMuted,
                marginBottom: space.sm,
              }}
            >
              Date of Birth (From Aadhaar)
            </label>
            <input
              type="text"
              placeholder="DD/MM/YYYY"
              value={
                form.dob ||
                verifiedPayload?.dob ||
                verifiedPayload?.dateOfBirth ||
                ""
              }
              readOnly
              style={{
                width: "100%",
                height: 48,
                boxSizing: "border-box",
                padding: "0 16px",
                fontSize: type.body.fontSize,
                fontFamily: font,
                color: C.textMuted,
                backgroundColor: C.surfaceSunken,
                border: `1.5px solid ${C.border}`,
                borderRadius: radii.control,
                outline: "none",
                cursor: "not-allowed",
              }}
            />
          </div>
        )}

        {isOfficer && (
          <div>
            <label
              style={{
                display: "block",
                fontSize: type.label.fontSize,
                lineHeight: type.label.lineHeight,
                fontWeight: type.label.fontWeight,
                color: C.textMuted,
                marginBottom: space.sm,
              }}
            >
              {role === "ORGANISATION_OFFICER"
                ? "Organisation"
                : "Academic Institution"}{" "}
              Name
            </label>
            <input
              type="text"
              placeholder="Verified Legal Entity Name"
              value={form.orgName}
              readOnly
              style={{
                width: "100%",
                height: 48,
                boxSizing: "border-box",
                padding: "0 16px",
                fontSize: type.body.fontSize,
                fontFamily: font,
                color: C.textMuted,
                backgroundColor: C.surfaceSunken,
                border: `1.5px solid ${C.border}`,
                borderRadius: radii.control,
                outline: "none",
                cursor: "not-allowed",
              }}
            />
            {fieldErrors?.orgName && (
              <p
                style={{
                  color: C.danger,
                  fontSize: type.caption.fontSize,
                  marginTop: 4,
                  marginBottom: 0,
                }}
              >
                {fieldErrors.orgName}
              </p>
            )}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: space.md }}>
          <div>
            <label
              style={{
                display: "block",
                fontSize: type.label.fontSize,
                lineHeight: type.label.lineHeight,
                fontWeight: type.label.fontWeight,
                color: C.textMuted,
                marginBottom: space.sm,
              }}
            >
              Email Address
            </label>
            <input
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              style={{
                width: "100%",
                height: 48,
                boxSizing: "border-box",
                padding: "0 16px",
                fontSize: type.body.fontSize,
                fontFamily: font,
                color: C.textStrong,
                backgroundColor: C.surface,
                border: `1.5px solid ${fieldErrors?.email ? C.danger : C.border}`,
                borderRadius: radii.control,
                outline: "none",
              }}
            />
            {fieldErrors?.email && (
              <p
                style={{
                  color: C.danger,
                  fontSize: type.caption.fontSize,
                  marginTop: 4,
                  marginBottom: 0,
                }}
              >
                {fieldErrors.email}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="register-mobile"
              style={{
                display: "block",
                fontSize: type.label.fontSize,
                lineHeight: type.label.lineHeight,
                fontWeight: type.label.fontWeight,
                color: C.textMuted,
                marginBottom: space.sm,
              }}
            >
              Mobile Number{" "}
              <span style={{ fontSize: type.caption.fontSize, color: C.textSubtle, fontWeight: 400 }}>
                {providerPhone ? "(verified)" : "(optional)"}
              </span>
            </label>
            {providerPhone ? (
              <div
                style={{
                  display: "flex",
                  height: 48,
                  borderRadius: radii.control,
                  border: `1.5px solid ${C.border}`,
                  backgroundColor: C.surfaceSunken,
                  overflow: "hidden",
                  boxSizing: "border-box",
                }}
              >
                <span
                  style={{
                    padding: "0 14px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: type.body.fontSize,
                    fontWeight: 600,
                    color: C.textMuted,
                    backgroundColor: C.divider,
                    borderRight: `1px solid ${C.border}`,
                  }}
                >
                  +91
                </span>
                <input
                  id="register-mobile"
                  type="tel"
                  value={providerPhone}
                  readOnly
                  style={{
                    width: "100%",
                    padding: "0 14px",
                    fontSize: type.body.fontSize,
                    fontFamily: font,
                    color: C.textMuted,
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    cursor: "not-allowed",
                  }}
                />
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    height: 48,
                    borderRadius: radii.control,
                    border: `1.5px solid ${fieldErrors?.phone ? C.danger : C.border}`,
                    backgroundColor: C.surface,
                    overflow: "hidden",
                    boxSizing: "border-box",
                  }}
                >
                  <span
                    style={{
                      padding: "0 14px",
                      display: "flex",
                      alignItems: "center",
                      fontSize: type.body.fontSize,
                      fontWeight: 600,
                      color: C.textMuted,
                      backgroundColor: C.surfaceSunken,
                      borderRight: `1px solid ${C.divider}`,
                    }}
                  >
                    +91
                  </span>
                  <input
                    id="register-mobile"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    maxLength={10}
                    placeholder="10-digit mobile"
                    value={form.phone}
                    onChange={(e) =>
                      update("phone", e.target.value.replace(/\D/g, ""))
                    }
                    style={{
                      width: "100%",
                      padding: "0 14px",
                      fontSize: type.body.fontSize,
                      fontFamily: font,
                      color: C.textStrong,
                      border: "none",
                      outline: "none",
                      background: "transparent",
                    }}
                  />
                </div>
                {!fieldErrors?.phone && (
                  <p
                    style={{
                      color: C.textSubtle,
                      fontSize: type.caption.fontSize,
                      marginTop: 4,
                      marginBottom: 0,
                    }}
                  >
                    Add a number if you wish to receive phone updates.
                  </p>
                )}
              </>
            )}
            {fieldErrors?.phone && (
              <p
                style={{
                  color: C.danger,
                  fontSize: type.caption.fontSize,
                  marginTop: 4,
                  marginBottom: 0,
                }}
              >
                {fieldErrors.phone}
              </p>
            )}
          </div>
        </div>

        <div>
          <label
            style={{
              display: "block",
              fontSize: type.label.fontSize,
              lineHeight: type.label.lineHeight,
              fontWeight: type.label.fontWeight,
              color: C.textMuted,
              marginBottom: space.sm,
            }}
          >
            Password
          </label>
          <div style={{ position: "relative" }}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Min 8 characters, with A-Z, a-z, 0-9"
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              style={{
                width: "100%",
                height: 48,
                boxSizing: "border-box",
                padding: "0 44px 0 16px",
                fontSize: type.body.fontSize,
                fontFamily: font,
                color: C.textStrong,
                backgroundColor: C.surface,
                border: `1.5px solid ${fieldErrors?.password ? C.danger : C.border}`,
                borderRadius: radii.control,
                outline: "none",
              }}
            />
            <button
              type="button"
              aria-label={showPassword ? "Hide password" : "Show password"}
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: 44,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                border: "none",
                color: C.textSubtle,
                cursor: "pointer",
                padding: 0,
              }}
            >
              {showPassword ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m3 3 18 18" />
                  <path d="M10.6 5.2A10.7 10.7 0 0 1 12 5c6 0 9.5 7 9.5 7a17.5 17.5 0 0 1-3.1 3.9" />
                  <path d="M6.2 6.3C3.9 8 2.5 12 2.5 12s3.5 7 9.5 7c1.5 0 2.8-.4 4-1" />
                  <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6Z" />
                  <circle cx="12" cy="12" r="2.5" />
                </svg>
              )}
            </button>
          </div>
          {fieldErrors?.password && (
            <p
              style={{
                color: C.danger,
                fontSize: type.caption.fontSize,
                marginTop: 4,
                marginBottom: 0,
              }}
            >
              {fieldErrors.password}
            </p>
          )}
        </div>

        {isOfficer && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: space.md }}>
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: type.label.fontSize,
                  lineHeight: type.label.lineHeight,
                  fontWeight: type.label.fontWeight,
                  color: C.textMuted,
                  marginBottom: space.sm,
                }}
              >
                Address Line 1{" "}
                <span style={{ fontSize: type.caption.fontSize, color: C.textSubtle, fontWeight: 400 }}>
                  ({verifiedPayload?.gstin || verifiedPayload?.verificationType === "GST" ? "verified via GST" : "verified via PAN"})
                </span>
              </label>
              <input
                type="text"
                placeholder="Address Line 1"
                value={form.addressLine1}
                readOnly
                style={{
                  width: "100%",
                  height: 48,
                  boxSizing: "border-box",
                  padding: "0 16px",
                  fontSize: type.body.fontSize,
                  fontFamily: font,
                  color: C.textMuted,
                  backgroundColor: C.surfaceSunken,
                  border: `1.5px solid ${C.border}`,
                  borderRadius: radii.control,
                  outline: "none",
                  cursor: "not-allowed",
                }}
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: type.label.fontSize,
                  lineHeight: type.label.lineHeight,
                  fontWeight: type.label.fontWeight,
                  color: C.textMuted,
                  marginBottom: space.sm,
                }}
              >
                City / State / Pincode
              </label>
              <div style={{ display: "flex", gap: space.sm }}>
                <input
                  type="text"
                  placeholder="City"
                  value={form.city}
                  readOnly
                  style={{
                    width: "35%",
                    height: 48,
                    boxSizing: "border-box",
                    padding: "0 12px",
                    fontSize: type.body.fontSize,
                    fontFamily: font,
                    color: C.textMuted,
                    backgroundColor: C.surfaceSunken,
                    border: `1.5px solid ${C.border}`,
                    borderRadius: radii.control,
                    outline: "none",
                    cursor: "not-allowed",
                  }}
                />
                <input
                  type="text"
                  placeholder="State"
                  value={form.state}
                  readOnly
                  style={{
                    width: "35%",
                    height: 48,
                    boxSizing: "border-box",
                    padding: "0 12px",
                    fontSize: type.body.fontSize,
                    fontFamily: font,
                    color: C.textMuted,
                    backgroundColor: C.surfaceSunken,
                    border: `1.5px solid ${C.border}`,
                    borderRadius: radii.control,
                    outline: "none",
                    cursor: "not-allowed",
                  }}
                />
                <input
                  type="text"
                  placeholder="Pincode"
                  value={form.pincode}
                  readOnly
                  style={{
                    width: "30%",
                    height: 48,
                    boxSizing: "border-box",
                    padding: "0 12px",
                    fontSize: type.body.fontSize,
                    fontFamily: font,
                    color: C.textMuted,
                    backgroundColor: C.surfaceSunken,
                    border: `1.5px solid ${C.border}`,
                    borderRadius: radii.control,
                    outline: "none",
                    cursor: "not-allowed",
                  }}
                />
              </div>
            </div>
          </div>
        )}

        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: space.sm,
            fontSize: type.caption.fontSize,
            color: C.textMuted,
            cursor: "pointer",
            marginTop: space.sm,
          }}
        >
          <input
            type="checkbox"
            checked={form.termsAccepted}
            onChange={(e) => update("termsAccepted", e.target.checked)}
            style={{ marginTop: 2, accentColor: C.primary }}
          />
          <span>I agree to the Terms of Service and Privacy Policy.</span>
        </label>
        {fieldErrors?.terms && (
          <p
            style={{
              color: C.danger,
              fontSize: type.caption.fontSize,
              margin: 0,
            }}
          >
            {fieldErrors.terms}
          </p>
        )}
      </div>

      {error && (
        <div
          style={{
            backgroundColor: C.dangerTint,
            color: C.danger,
            padding: `${space.sm}px ${space.md}px`,
            borderRadius: radii.control,
            marginTop: space.md,
            fontSize: type.caption.fontSize,
            lineHeight: type.caption.lineHeight,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ marginTop: space.xl }}>
        <BrandButton
          type="submit"
          variant="primary"
          fullWidth
          loading={completing}
        >
          {completing ? "Creating account..." : roleConfig.completeButton}
        </BrandButton>
      </div>

      <div
        style={{
          marginTop: space.lg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          color: C.textSubtle,
          fontSize: type.caption.fontSize,
        }}
      >
        <LockIcon size={14} />
        <span>Verified at source via Government and official APIs</span>
      </div>
    </form>
  );
}
